export type LogContext = Record<string, unknown>;

export type LogLevelName = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export interface ResolvedError {
  /** Plain object passed as the error argument, treated as metadata. */
  context?: LogContext;
  /** Normalized error metadata (errorId/errorType/errorMessage/stack). */
  fields?: LogContext;
  /** The original error object when one was supplied. */
  errorObject?: unknown;
}

export const isPlainObject = (value: unknown): value is LogContext =>
  value !== null &&
  typeof value === 'object' &&
  !(value instanceof Error) &&
  !Array.isArray(value);

export const createErrorId = (): string => {
  try {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }
  } catch {
    // fall back to a non-crypto id below
  }
  return `err_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const isDevelopment = process.env.NODE_ENV === 'development';

export const resolveError = (error: unknown): ResolvedError => {
  if (error === undefined || error === null) {
    return {};
  }

  if (error instanceof Error) {
    const fields: LogContext = {
      errorId: createErrorId(),
      errorType: error.name,
      errorMessage: error.message,
    };
    if (isDevelopment && error.stack) {
      fields.stack = error.stack;
    }
    return { fields, errorObject: error };
  }

  if (typeof error === 'string') {
    return {
      fields: { errorId: createErrorId(), errorMessage: error },
      errorObject: error,
    };
  }

  if (isPlainObject(error)) {
    return { context: error };
  }

  return {
    fields: { errorId: createErrorId(), errorMessage: String(error) },
    errorObject: error,
  };
};
